import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Heart, Calendar, Gift, Map, Globe } from 'lucide-react';
import './Header.css';
import { LanguageContext } from '../LanguageContext';

const Header: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState('/');
    const [menuOpen, setMenuOpen] = useState(false);
    const { language, setLanguage } = useContext(LanguageContext);

    // Translations for the header
    const translations = {
        en: {
            home: 'Home',
            rsvp: 'RSVP',
            registry: 'Registry',
            venue: 'Venue'
        },
        ro: {
            home: 'Acasă',
            rsvp: 'Confirmare',
            registry: 'Wishlist',
            venue: 'Locație'
        }
    };

    const t = translations[language];

    useEffect(() => {
        setActiveSection(location.pathname);
        
        // Check if URL doesn't have language parameter and add it
        const urlParams = new URLSearchParams(location.search);
        if (!urlParams.has('lang') && language) {
            urlParams.set('lang', language);
            navigate(`${location.pathname}?${urlParams.toString()}`, { replace: true });
        }
    }, [location, language, navigate]);

    const toggleLanguage = () => {
        const newLanguage = language === 'ro' ? 'en' : 'ro';
        setLanguage(newLanguage);
        
        // Navigate to the same path but with updated language parameter
        const urlParams = new URLSearchParams(location.search);
        urlParams.set('lang', newLanguage);
        navigate(`${location.pathname}?${urlParams.toString()}`, { replace: true });
    };

    // Preserve the language parameter when navigating
    const getLanguagePreservedLink = (path: string) => {
        const urlParams = new URLSearchParams(location.search);
        const langParam = urlParams.get('lang');
        
        if (langParam) {
            return `${path}?lang=${langParam}`;
        }
        return path;
    };

    const navItems = [
        { id: 'home', label: t.home, link: '/', icon: Heart },
        { id: 'rsvp', label: t.rsvp, link: '/rsvp', icon: Calendar },
        { id: 'registry', label: t.registry, link: '/wishlist', icon: Gift },
        { id: 'venue', label: t.venue, link: '/venue', icon: Map },
    ];

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    return (
        <header className="header">
            <div className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50 transition-all duration-300">
                {/* Decorative elements */}
                <div className="absolute left-0 top-0 w-24 h-24 overflow-hidden">
                    <img src="/images/martini-glass.svg" alt="" className="w-full h-full object-contain opacity-70" />
                </div>
                <div className="absolute right-0 top-0 w-24 h-24 overflow-hidden">
                    <img src="/images/flowers.svg" alt="" className="w-full h-full object-contain opacity-70 scale-x-[-1]" />
                </div>

                <div className="max-w-6xl mx-auto px-6 relative">
                    <nav className="flex items-center justify-between h-20">
                        {/* Logo/Brand with script font styling to match invitation */}
                        <div className="flex items-center">
                            <Link to={getLanguagePreservedLink('/')} className="font-serif italic text-2xl md:text-3xl tracking-wide text-gray-800">
                                Adina & Răzvan
                            </Link>
                        </div>

                        {/* Language Switcher - Mobile */}
                        <button 
                            onClick={toggleLanguage}
                            className="md:hidden flex items-center mr-3 p-2 rounded-full text-gray-700 hover:bg-gray-100 focus:outline-none"
                            aria-label="Toggle language"
                        >
                            <Globe className="w-5 h-5" />
                            <span className="ml-1 text-sm">{language === 'ro' ? 'EN' : 'RO'}</span>
                        </button>

                        {/* Mobile menu button */}
                        <button 
                            onClick={toggleMenu}
                            className="md:hidden flex items-center p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none"
                            aria-label="Toggle menu"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
                                {menuOpen ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                                )}
                            </svg>
                        </button>

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex space-x-6">
                            {navItems.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <Link 
                                        key={item.id} 
                                        to={getLanguagePreservedLink(item.link)} 
                                        className="focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50 rounded-lg"
                                    >
                                        <div
                                            className={`flex items-center px-3 py-2 rounded-lg transition-colors duration-300 ${
                                                activeSection === item.link
                                                    ? 'text-gray-900 font-medium'
                                                    : 'text-gray-600 hover:text-gray-900'
                                            }`}
                                        >
                                            <Icon className="w-4 h-4 mr-2"/>
                                            <span className="text-sm font-medium">{item.label}</span>
                                            {activeSection === item.link && (
                                                <div className="h-0.5 w-full absolute -bottom-2 left-0 bg-gray-800 rounded-full"></div>
                                            )}
                                        </div>
                                    </Link>
                                );
                            })}

                            {/* Language Switcher - Desktop */}
                            <button 
                                onClick={toggleLanguage}
                                className="flex items-center px-3 py-2 rounded-lg text-gray-600 hover:text-gray-900 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50"
                                aria-label="Toggle language"
                            >
                                <Globe className="w-4 h-4 mr-2" />
                                <span className="text-sm font-medium">{language === 'ro' ? 'EN' : 'RO'}</span>
                            </button>
                        </div>
                    </nav>

                    {/* Mobile menu */}
                    <div 
                        className={`md:hidden absolute left-0 right-0 bg-white shadow-md transition-all duration-300 ${
                            menuOpen ? 'max-h-64 py-4 opacity-100' : 'max-h-0 py-0 opacity-0 overflow-hidden'
                        }`}
                    >
                        <div className="px-4 space-y-2">
                            {navItems.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <Link 
                                        key={item.id} 
                                        to={getLanguagePreservedLink(item.link)} 
                                        className="block"
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        <div
                                            className={`flex items-center py-3 px-4 rounded-lg ${
                                                activeSection === item.link
                                                    ? 'bg-gray-50 text-gray-900 font-medium'
                                                    : 'text-gray-600'
                                            }`}
                                        >
                                            <Icon className="w-5 h-5 mr-3"/>
                                            <span>{item.label}</span>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;